import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useDocumentsForCurrentUserQuery, } from "generated/generated-models";
import { Autocomplete, Box, FormControl, Paper, TextField, } from "@mui/material";
import { DataGridPro, GridLogicOperator, } from "@mui/x-data-grid-pro";
import { CError } from "app/common/CError";
import { Loading } from "app/lib/components/Loading";
import { useState } from "react";
import { useMediaQuery } from "@mui/material";
import { DownloadLink } from "app/components/DownloadLink";
import dayjs from "dayjs";
import NoRowsOverlay from "app/components/NoRowsOverlay";
const filterDocumentByAccount = (filterByAccount, documents) => {
    if (documents == null)
        return [];
    const accountsByPartyId = documents.financeSummary.Instruments.flatMap((instrument) => instrument.Accounts);
    let filteredAccounts = accountsByPartyId;
    if (filterByAccount != null && filterByAccount !== "") {
        filteredAccounts = filteredAccounts.filter((account) => account.Number === filterByAccount);
    }
    const filteredDocumentsArray = filteredAccounts.flatMap((account) => account.Documents.map((document) => ({
        InstrumentId: account.InstrumentId ?? "",
        AccountNumber: account.Number ?? "",
        FileName: document.Document?.FileName ?? "",
        Id: document.Document?.Id ?? "",
        Date: dayjs(document.Document?.CreatedOn),
    })));
    if (filterByAccount == null || filterByAccount === "") {
        // Add documents that are from instrument level
        documents.financeSummary.Instruments.forEach((instrument) => {
            instrument.Documents.forEach((document) => {
                if (filteredDocumentsArray.find((x) => x.Id === document.Document?.Id) === undefined) {
                    filteredDocumentsArray.push({
                        InstrumentId: instrument.Id,
                        AccountNumber: "",
                        FileName: document.Document?.FileName ?? "",
                        Id: document.Document?.Id ?? "",
                        Date: dayjs(document.Document?.CreatedOn),
                    });
                }
            });
        });
        // Add the documents that are not associated with an account
        documents.userSession.Party.Documents.forEach((document) => {
            if (filteredDocumentsArray.find((x) => x.Id === document.Document?.Id) === undefined) {
                filteredDocumentsArray.push({
                    InstrumentId: "",
                    AccountNumber: "",
                    FileName: document.Document?.FileName ?? "",
                    Id: document.Document?.Id ?? "",
                    Date: dayjs(document.Document?.CreatedOn),
                });
            }
        });
    }
    return filteredDocumentsArray.sort((a, b) => a.Date.isBefore(b.Date) ? 1 : -1);
};
const DocumentsPage = () => {
    const [selectedAccountNumber, setSelectedAccountNumber] = useState("");
    const { data, loading, error } = useDocumentsForCurrentUserQuery({
        fetchPolicy: "network-only",
    });
    const widthSml = useMediaQuery("(max-width: 450px)");
    const accountNumbers = data?.financeSummary.Instruments.flatMap((instrument) => instrument.Accounts).map((account) => String(account.Number)) ?? [];
    const documents = filterDocumentByAccount(selectedAccountNumber, data);
    if (loading) {
        return _jsx(Loading, {});
    }
    if (error) {
        return (_jsx("section", { className: "sub-wrap", children: _jsx(CError, { error: "Could not retrieve account documents." }) }));
    }
    const columns = [
        {
            field: "AccountNumber",
            headerName: "Account",
            minWidth: 200,
            flex: 1,
            filterable: true,
        },
        {
            field: "FileName",
            headerName: "Document",
            minWidth: 600,
            flex: 1,
            filterable: true,
        },
        {
            field: "actions",
            type: "actions",
            width: 120,
            resizable: false,
            renderCell: (params) => (_jsx(DownloadLink, { href: `/api/statements/documents/${params.value}` })),
        },
    ];
    return (_jsxs("section", { className: "sub-wrap statements-list", children: [_jsx("div", { className: "list-header", children: _jsx("h2", { children: "Documents" }) }), _jsx(Box, { children: _jsxs("div", { className: "statement-container", style: { width: "100%" }, children: [_jsx(Box, { sx: { flex: "1 1 100%" }, className: "filterContainer", children: _jsx(FormControl, { sx: { width: 220, mr: 3 }, size: "small", className: "filterControl", children: _jsx(Autocomplete, { id: "filtered-account-outlined", size: "small", options: accountNumbers, getOptionLabel: (option) => option ? option : "", value: selectedAccountNumber, onChange: (_event, newSelectedAccountNumber) => {
                                        setSelectedAccountNumber(newSelectedAccountNumber ?? "");
                                    }, PaperComponent: ({ children }) => (_jsx(Paper, { style: { fontSize: "0.9rem" }, children: children })), renderInput: (params) => (_jsx(TextField, { ...params, label: "Account" })) }) }) }), _jsx(DataGridPro, { className: "custom-grid", columns: columns, columnVisibilityModel: {
                                Id: !widthSml,
                            }, rows: documents, getRowId: (r) => r.Id, autoHeight: true, pagination: true, pageSizeOptions: [10, 25, 50], initialState: {
                                pagination: {
                                    paginationModel: { pageSize: 10 },
                                },
                                pinnedColumns: { right: ["actions"] },
                            }, slots: {
                                noRowsOverlay: () => (_jsx(NoRowsOverlay, { children: "There are currently no documents for this account." })),
                            }, disableColumnSelector: true, slotProps: {
                                filterPanel: {
                                    logicOperators: [GridLogicOperator.And],
                                    columnsSort: "asc",
                                    filterFormProps: {
                                        logicOperatorInputProps: {
                                            variant: "outlined",
                                            size: "small",
                                        },
                                        columnInputProps: {
                                            variant: "outlined",
                                            size: "small",
                                            sx: { mt: "auto" },
                                        },
                                        operatorInputProps: {
                                            variant: "outlined",
                                            size: "small",
                                            sx: { mt: "auto" },
                                        },
                                        deleteIconProps: {
                                            sx: {
                                                "& .MuiSvgIcon-root": {
                                                    color: "#d32f2f",
                                                },
                                            },
                                        },
                                    },
                                    sx: {
                                        "& .MuiDataGrid-filterForm": { p: 2 },
                                        "& .MuiDataGrid-filterForm:nth-child(even)": {
                                            backgroundColor: (theme) => theme.palette.mode === "dark"
                                                ? "#444"
                                                : "#f5f5f5",
                                        },
                                        "& .MuiDataGrid-filterFormLinkOperatorInput": { mr: 2 },
                                        "& .MuiDataGrid-filterFormColumnInput": {
                                            mr: 2,
                                            width: 150,
                                        },
                                        "& .MuiDataGrid-filterFormOperatorInput": {
                                            mr: 2,
                                        },
                                        "& .MuiDataGrid-filterFormValueInput": {
                                            width: 200,
                                        },
                                    },
                                },
                            } })] }) })] }));
};
export default DocumentsPage;
